import {discountCentsFromPercent,discountPercentValue} from './discountPercent.js';
export const MAX_CART_ITEMS = 200;
export const PAYMENT_METHODS = ['cash', 'pix', 'card', 'other'];
export const PRODUCT_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const PRODUCT_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

export function productPhotoExtension(mimeType = '') {
  return (
    {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/heic': 'heic',
      'image/heif': 'heif',
    }[String(mimeType).toLowerCase()] || null
  );
}

export function validateProductPhoto(file) {
  if (!file) return { valid: false, error: 'Selecione uma fotografia do produto.' };
  const type = String(file.type || '').toLowerCase();
  if (!PRODUCT_PHOTO_MIME_TYPES.includes(type)) {
    return { valid: false, error: 'Use uma imagem JPEG, PNG, WebP, HEIC ou HEIF.' };
  }
  if (Number(file.size || 0) <= 0 || Number(file.size) > PRODUCT_PHOTO_MAX_BYTES) {
    return { valid: false, error: 'A fotografia deve ter no máximo 8 MB.' };
  }
  return { valid: true, type, extension: productPhotoExtension(type) };
}

export function productPhotoPath(organizationId, objectId, mimeType) {
  const organization = String(organizationId || '').trim();
  const object = String(objectId || '').replace(/[^0-9A-Za-z-]/g, '');
  const extension = productPhotoExtension(mimeType);
  if (!organization || !object || !extension) return null;
  return `${organization}/products/${object}.${extension}`;
}


export const MAX_PRODUCT_PHOTOS = 3;

// Two extras live in metadata.gallery_images; image_path stays the canonical cover
// for K-Repair and existing checkout/product consumers.
export function normalizeGalleryImages(images, coverPath, organizationId) {
  const prefix = organizationId ? `${organizationId}/products/` : null;
  const seen = new Set([coverPath].filter(Boolean));
  const items = [];
  for (const image of Array.isArray(images) ? images : []) {
    const path = String(image?.path || '');
    if (!path || seen.has(path) || path.includes('..') || path.includes('//') ||
      (prefix && !path.startsWith(prefix))) continue;
    seen.add(path);
    items.push({ path, mime_type: String(image?.mime_type || 'image/webp') });
    if (items.length === MAX_PRODUCT_PHOTOS - 1) break;
  }
  return items;
}

export function applyVisualProductSuggestion(form, suggestion, categories = []) {
  if (!suggestion || typeof suggestion !== 'object') return form;
  const clean = key => typeof suggestion[key] === 'string' ? suggestion[key].trim().slice(0, 1500) : '';
  const candidate = normalizeBarcode(clean('barcode_visible'));
  const barcode = candidate && isValidGtin(candidate) ? candidate : '';
  const category = categories.find(item =>
    item.name?.trim().toLocaleLowerCase('pt-BR') === clean('category').toLocaleLowerCase('pt-BR'));
  const commerce = form.metadata?.commerce || {};
  const keywords = Array.isArray(suggestion.search_keywords)
    ? suggestion.search_keywords.filter(k=>typeof k === 'string').map(k=>k.slice(0, 100)).slice(0, 14) : [];
  const proposedUse = clean('local_usage');
  const localLabel = text => String(text||'').normalize('NFD')
    .replace(/[\u0300-\u036f]/g,'').toLocaleLowerCase('pt-BR').trim();
  const genericSegment = new Set([
    localLabel(clean('category')),localLabel(clean('subcategory')),
    'confeitaria','bebida','alimento','alimentos'
  ]);
  const reviewedUse = genericSegment.has(localLabel(proposedUse)) ? '' : proposedUse;
  const nextCommerce = {
    ...commerce,
    online_title: commerce.online_title || clean('online_title'),
    online_description: commerce.online_description || clean('online_description'),
    model_variant: commerce.model_variant || clean('model_variant'),
    package_size: commerce.package_size || clean('package_size'),
    local_usage: commerce.local_usage || reviewedUse,
    category_suggestion: commerce.category_suggestion || clean('category'),
    search_keywords: commerce.search_keywords?.length ? commerce.search_keywords : keywords,
  };
  return {
    ...form,
    name: form.name || clean('name'),
    description: form.description || clean('description'),
    brand: form.brand || clean('brand'),
    barcode: form.barcode || barcode,
    category_id: form.category_id || category?.id || '',
    metadata: {...(form.metadata || {}),commerce:nextCommerce},
  };
}

export function quantity(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * 1000) / 1000;
}

export function addToCart(items, product) {
  if (!product?.id || product.active === false) return items;
  const found = items.find((item) => item.product_id === product.id);
  if (found) {
    return items.map((item) =>
      item.product_id === product.id
        ? { ...item, quantity: quantity(item.quantity) + 1 }
        : item,
    );
  }
  if (items.length >= MAX_CART_ITEMS) return items;
  return [
    ...items,
    {
      product_id: product.id,
      sku: product.sku,
      barcode: product.barcode || null,
      name: product.name,
      unit: product.unit || 'UN',
      quantity: 1,
      unit_price_cents: Number(product.price_cents || 0),
      discount_cents: 0,
      allow_fractional: Boolean(product.allow_fractional),
      track_inventory: Boolean(product.track_inventory),
    },
  ];
}

export function setQuantity(items, productId, next) {
  const value = quantity(next);
  if (!value) return items.filter((item) => item.product_id !== productId);
  return items.map((item) => {
    if (item.product_id !== productId) return item;
    const gross = Math.round(value * Number(item.unit_price_cents || 0));
    return {
      ...item, quantity: value,
      ...(item.discount_percent != null
        ? { discount_cents: discountCentsFromPercent(gross, item.discount_percent) }
        : {}),
    };
  });
}

export function setLineDiscountPercent(items, productId, percent) {
  const normalized=discountPercentValue(percent);
  return items.map((item) => {
    if (item.product_id!==productId)return item;
    const gross=Math.round(quantity(item.quantity)*Number(item.unit_price_cents||0));
    return { ...item, discount_percent:normalized,
      discount_cents:discountCentsFromPercent(gross,normalized) };
  });
}

export function setLineDiscount(items, productId, discountCents) {
  const value = Math.max(0, Math.round(Number(discountCents || 0)));
  return items.map((item) => {
    if (item.product_id !== productId) return item;
    const gross = Math.round(quantity(item.quantity) * Number(item.unit_price_cents || 0));
    return { ...item, discount_cents: Math.min(gross, value) };
  });
}

export function totals(items = [], orderDiscountCents = 0) {
  const subtotalCents = items.reduce(
    (sum, item) => sum + Math.round(quantity(item.quantity) * Number(item.unit_price_cents || 0)),
    0,
  );
  const lineDiscountCents = items.reduce(
    (sum, item) => sum + Math.max(0, Number(item.discount_cents || 0)),
    0,
  );
  const discountCents = Math.min(
    subtotalCents,
    lineDiscountCents + Math.max(0, Number(orderDiscountCents || 0)),
  );
  return {
    subtotalCents,
    lineDiscountCents,
    orderDiscountCents: Math.max(0, Number(orderDiscountCents || 0)),
    discountCents,
    totalCents: Math.max(0, subtotalCents - discountCents),
  };
}

export function paymentTotals(payments = [], saleTotalCents = 0) {
  const tenderedCents = payments.reduce(
    (sum, payment) => sum + Math.max(0, Number(payment.amount_cents || 0)),
    0,
  );
  const cashCents = payments
    .filter((payment) => payment.method === 'cash')
    .reduce((sum, payment) => sum + Math.max(0, Number(payment.amount_cents || 0)), 0);
  const changeCents = Math.max(0, tenderedCents - Number(saleTotalCents || 0));
  return {
    tenderedCents,
    cashCents,
    changeCents,
    valid:
      payments.length > 0 &&
      payments.every(
        (payment) =>
          PAYMENT_METHODS.includes(payment.method) && Number(payment.amount_cents || 0) > 0,
      ) &&
      tenderedCents >= Number(saleTotalCents || 0) &&
      changeCents <= cashCents,
  };
}

export function checkoutPayload({
  organizationId,
  locationId,
  terminalId,
  sessionId,
  items,
  payments,
  customerId,
  notes,
  discountCents = 0,
  idempotencyKey,
  source = 'smart-pdv',
}) {
  const value = totals(items, discountCents);
  return {
    organization_id: organizationId,
    location_id: locationId,
    terminal_id: terminalId,
    register_session_id: sessionId,
    idempotency_key: idempotencyKey,
    customer_id: customerId || null,
    notes: notes?.trim() || null,
    order_discount_cents: Math.max(0, Number(discountCents || 0)),
    items: items.map((item) => ({
      product_id: item.product_id,
      quantity: quantity(item.quantity),
      discount_cents: Math.max(0, Number(item.discount_cents || 0)),
    })),
    payments: payments.map((payment) => ({
      method: payment.method,
      amount_cents: Math.max(0, Number(payment.amount_cents || 0)),
      external_provider: payment.external_provider || null,
      external_reference: payment.external_reference || null,
    })),
    expected_total_cents: value.totalCents,
    receipt_type: 'non_fiscal',
    integration_metadata: {
      source,
      client_created_at: new Date().toISOString(),
      offline_replay: source === 'smart-pdv-offline',
    },
  };
}

export function money(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(
    Number(cents || 0) / 100,
  );
}

export function centsFromDecimal(value) {
  if (typeof value === 'number') return Math.round(value * 100);
  const raw = String(value ?? '').trim();
  if (!raw) return 0;
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(/[^0-9.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

export function decimalFromCents(cents) {
  return (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
}

export function normalizeBarcode(value = '') {
  return String(value).replace(/[^0-9A-Za-z]/g, '').slice(0, 64);
}

export function gtinCheckDigit(value = '') {
  const digits = normalizeBarcode(value);
  if (!/^\d{7,13}$/.test(digits)) return null;
  const body = digits.slice(0, -1);
  const sum = [...body]
    .reverse()
    .reduce((total, digit, index) => total + Number(digit) * (index % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10;
}

export function isValidGtin(value = '') {
  const digits = normalizeBarcode(value);
  if (![8, 12, 13, 14].includes(digits.length) || !/^\d+$/.test(digits)) return false;
  return gtinCheckDigit(digits) === Number(digits.at(-1));
}

export async function detectBarcodeFromImage(file, runtime = globalThis) {
  const Detector = runtime?.BarcodeDetector;
  const createBitmap = runtime?.createImageBitmap;
  if (!file) return null;
  if (typeof Detector === 'function' && typeof createBitmap === 'function') {
    try {
      const supported = await Detector.getSupportedFormats?.().catch(() => []);
      const preferred = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];
      const formats = preferred.filter((format) => !supported?.length || supported.includes(format));
      const detector = new Detector(formats.length ? { formats } : undefined);
      const bitmap = await createBitmap(file);
      const found = await detector.detect(bitmap);
      bitmap.close?.();
      const nativeValue = normalizeBarcode(found?.[0]?.rawValue || '');
      if (nativeValue) return nativeValue;
    } catch {
      // A leitura portátil abaixo cobre navegadores sem BarcodeDetector estável.
    }
  }
  if (runtime !== globalThis || !runtime.URL?.createObjectURL) return null;
  let objectUrl;
  try {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    objectUrl = runtime.URL.createObjectURL(file);
    const result = await new BrowserMultiFormatReader().decodeFromImageUrl(objectUrl);
    return normalizeBarcode(result?.getText?.() || '') || null;
  } catch {
    return null;
  } finally {
    if (objectUrl) runtime.URL.revokeObjectURL(objectUrl);
  }
}

export function expectedCash(movements = [], sessionId) {
  return movements
    .filter((movement) => !sessionId || movement.session_id === sessionId)
    .reduce((sum, movement) => {
      const amount = Number(movement.amount_cents || 0);
      return ['withdrawal', 'cancellation', 'refund'].includes(movement.movement_type)
        ? sum - amount
        : sum + amount;
    }, 0);
}

export function stockFor(balances = [], productId, locationId) {
  return Number(
    balances.find(
      (balance) => balance.product_id === productId && (!locationId || balance.location_id === locationId),
    )?.quantity || 0,
  );
}

export function cartStockErrors(items = [], balances = [], locationId, allowNegative = false) {
  if (allowNegative) return [];
  return items
    .filter((item) => item.track_inventory)
    .filter((item) => quantity(item.quantity) > stockFor(balances, item.product_id, locationId))
    .map((item) => item.name);
}

export function isRetryableCheckoutError(error, online = globalThis.navigator?.onLine) {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    online === false ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('load failed')
  );
}

export function translateMovement(type) {
  return (
    {
      opening: 'Abertura',
      sale: 'Venda em dinheiro',
      supply: 'Suprimento',
      withdrawal: 'Sangria',
      cancellation: 'Cancelamento',
      refund: 'Estorno',
      closing: 'Fechamento',
      adjustment: 'Ajuste',
      sale_void: 'Venda cancelada',
    }[type] || type
  );
}
